<template>
  <header class="header" :class="{ scrolled: isScrolled }">
    <div class="header-container">
      <!-- Logo Section -->
      <div class="logo-section">
        <img src="/logo-tutors.png" alt="Logo" class="tutorlogo-image" />
      </div>

      <!-- Navigation Links -->
      <nav class="navigation">
        <router-link to="/tutor/sessions" class="nav-link" active-class="active"
          >My Sessions</router-link
        >
        <router-link to="/tutor-messages" class="nav-link messages-link" active-class="active">
          Messages
          <span
            v-if="hasUnreadBadge"
            class="unread-badge"
            :title="`${displayUnreadCount} conversation${displayUnreadCount === 1 ? '' : 's'} with unread messages`"
          >
            {{ displayUnreadCount }}
          </span>
        </router-link>
      </nav>

      <!-- Action Button / User Menu (tutor) -->
      <div class="action-buttons">
        <!-- Only show user menu when logged in - this is a protected route -->
        <div v-if="isLoggedIn" ref="menuRef" class="user-menu">
          <button class="user-button" type="button" @click="toggleUserMenu">
            <span class="user-name">{{ userDisplayName }}</span>
            <svg
              class="dropdown-arrow"
              width="16"
              height="16"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 011.08 1.04l-4.25 4.25a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
          <div v-show="isUserMenuOpen" class="user-dropdown">
            <router-link to="/tutor/profile" class="user-dropdown-link" @click="closeUserMenu">
              <svg
                class="w-4 h-4 text-gray-500 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                  clip-rule="evenodd"
                />
              </svg>
              Profile
            </router-link>

            <div class="border-t border-gray-200"></div>

            <button class="user-dropdown-link" type="button" @click="handleChangePassword">
              <svg
                class="w-4 h-4 text-gray-500 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1 1 21 9z"
                />
              </svg>
              Change Password
            </button>

            <div class="border-t border-gray-200"></div>

            <button class="user-dropdown-link" type="button" @click="handleLogout">
              <svg
                class="w-4 h-4 text-red-500 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Log out
            </button>
          </div>
        </div>
        <!-- Show loading state when authentication is being verified -->
        <div v-else class="user-menu">
          <div class="user-button-loading">
            <span class="user-name">Loading...</span>
          </div>
        </div>
      </div>

      <!-- Mobile Hamburger Menu -->
      <button class="mobile-menu-toggle" @click="toggleMobileMenu">
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
      </button>
    </div>

    <!-- Mobile Navigation Menu -->
    <div class="mobile-nav" :class="{ active: isMobileMenuOpen }">
      <div class="mobile-nav-content">
        <router-link to="/tutor/sessions" class="mobile-nav-link" @click="closeMobileMenu"
          >My Sessions</router-link
        >
        <router-link to="/tutor-messages" class="mobile-nav-link messages-link" @click="closeMobileMenu">
          Messages
          <span
            v-if="hasUnreadBadge"
            class="unread-badge mobile"
            :title="`${displayUnreadCount} conversation${displayUnreadCount === 1 ? '' : 's'} with unread messages`"
          >
            {{ displayUnreadCount }}
          </span>
        </router-link>
        <div class="border-t border-white/20 my-2"></div>
        <router-link to="/tutor/profile" class="mobile-nav-link" @click="closeMobileMenu"
          >Profile</router-link
        >
        <button class="mobile-nav-link" type="button" @click="handleChangePassword">
          Change Password
        </button>
        <button 
          v-if="isLoggedIn" 
          class="mobile-nav-link logout-button" 
          type="button" 
          @click="handleLogout"
        >
          Log out
        </button>
      </div>
    </div>
  </header>

  <!-- Toast Notifications -->
  <ToastContainer />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { auth, db, onAuthStateChanged, sendPasswordResetEmail, doc, getDoc } from "@/firebase";
import { signOut } from "firebase/auth";
import { useToast } from "@/composables/useToast";
import { useMessagingStore } from "@/stores/messagingStore";
import { useMessagingSync } from "@/composables/useMessagingSync";
import { storeToRefs } from "pinia";
import { messagingPermissionService } from "@/services/messagingPermissionService";
import { ensureInitializedAndLoggedIn, fetchUnreadMessageCountMap, subscribeToUnreadMessages, logoutCometChat } from "@/services/cometchat";
import ToastContainer from "@/components/ToastContainer.vue";
import { logDebug, logInfo, logWarn, logError } from "@/utils/secureLogger";
import "@/assets/main.css";

// Router
const router = useRouter();
const route = useRoute();

// Toast notifications
const { showSuccess, showError } = useToast();

// Messaging store and sync
const messagingStore = useMessagingStore();
const { unreadConversationCount } = storeToRefs(messagingStore);
const { broadcastConversationRead, broadcastUnreadCountsUpdate } = useMessagingSync('tutor');

// Guard to prevent badge from showing stale counts during initialization
const unreadReady = ref(false);
const displayUnreadCount = computed(() => {
  const raw = unreadConversationCount.value;
  const cap = contactIds.value.size || raw;
  const result = Math.min(raw, cap);
  logDebug('💠 Badge count computed', { 
    raw, 
    cap, 
    result,
    unreadReady: unreadReady.value 
  });
  return result;
});
const hasUnreadBadge = computed(() => unreadReady.value && displayUnreadCount.value > 0);

// Watch for changes in unreadConversationCount to debug badge updates
watch(unreadConversationCount, (newVal, oldVal) => {
  logInfo('🔔 Total unread conversations changed in tutor header', {
    oldValue: oldVal,
    newValue: newVal,
    displayCount: displayUnreadCount.value,
    hasUnreadBadge: hasUnreadBadge.value
  });
});

// Contact IDs for filtering unread counts (only count conversations with contacts)
const contactIds = ref<Set<string>>(new Set());

const syncContactIds = async (userId: string) => {
  try {
    const contacts = await messagingPermissionService.getTutorMessagingContacts(userId);
    contactIds.value = new Set(contacts.map((c: any) => c.id.toLowerCase()));
    logDebug('Synced messaging contact IDs for tutor badge filtering', { count: contactIds.value.size });
    return contactIds.value;
  } catch (error) {
    logWarn('Failed to load contact IDs for tutor unread filtering', { error });
    return contactIds.value;
  }
};

// Load explicitly read conversations from localStorage (shared with TutorMessagesView)
const EXPLICITLY_READ_KEY = 'elevateED_explicitly_read_tutor';
const getExplicitlyReadConversations = (): Set<string> => {
  try {
    const stored = localStorage.getItem(EXPLICITLY_READ_KEY);
    const conversations = stored ? new Set<string>(JSON.parse(stored)) : new Set<string>();
    logDebug('📦 Loaded explicitly read conversations in tutor header', {
      count: conversations.size,
      conversations: Array.from(conversations).slice(0, 5).map(id => id.substring(0, 8) + '***')
    });
    return conversations;
  } catch (error) {
    logWarn('Failed to load explicitly read conversations in tutor header', { error });
    return new Set<string>();
  }
};

const filterToContacts = (unreadMap: Record<string, number>) => {
  if (!unreadMap) return {};
  if (!contactIds.value.size) return unreadMap; // if we don't know contacts yet, don't drop counts
  const currentUserId = auth.currentUser?.uid?.toLowerCase();
  const explicitlyRead = getExplicitlyReadConversations();
  const filtered: Record<string, number> = {};
  
  // Load recently marked read timestamps to avoid race conditions
  let recentlyMarkedRead: Record<string, number> = {};
  try {
    const stored = localStorage.getItem('elevateED_recently_marked_read_tutor');
    if (stored) {
      recentlyMarkedRead = JSON.parse(stored);
    }
  } catch {}
  
  logDebug('🔍 Filtering unread map in tutor header', {
    totalUnread: Object.keys(unreadMap).length,
    explicitlyReadCount: explicitlyRead.size,
    explicitlyReadIds: Array.from(explicitlyRead).slice(0, 5).map(id => id.substring(0, 8) + '***'),
    unreadMapIds: Object.keys(unreadMap).slice(0, 5).map(id => id.substring(0, 8) + '***')
  });
  
  const now = Date.now();
  
  Object.entries(unreadMap).forEach(([id, count]) => {
    const key = String(id || '').toLowerCase();
    
    // Check if this was explicitly marked as read
    const wasExplicitlyRead = explicitlyRead.has(key);
    
    // Check if this was recently marked as read (within last 60 seconds)
    // This gives CometChat plenty of time to process the "mark as read" request
    const recentlyMarked = recentlyMarkedRead[key] && (now - recentlyMarkedRead[key] < 60000);
    
    // DON'T remove from explicitly read set just because CometChat shows unread.
    // Only remove when a REAL new message arrives (handled by handleIncomingUnread listener).
    // The recentlyMarked check provides extra protection during CometChat's sync delay.
    if (wasExplicitlyRead && recentlyMarked) {
      logDebug('⏳ Protecting recently marked conversation from removal', {
        conversationId: id.substring(0, 8) + '***',
        ageMs: now - recentlyMarkedRead[key]
      });
    }
    
    // Skip self-conversations, non-contacts, and explicitly read conversations
    if (key !== currentUserId && contactIds.value.has(key) && count > 0 && !explicitlyRead.has(key)) {
      filtered[id] = count;
    } else if (explicitlyRead.has(key) && count > 0) {
      logDebug('✅ Filtering out explicitly read conversation from tutor badge', {
        conversationId: id.substring(0, 8) + '***',
        cometchatCount: count,
        reason: 'explicitly read by user'
      });
    } else if (key === currentUserId) {
      logDebug('Filtering out self-conversation', {
        conversationId: id.substring(0, 8) + '***'
      });
    } else if (!contactIds.value.has(key)) {
      logDebug('Filtering out non-contact', {
        conversationId: id.substring(0, 8) + '***'
      });
    }
  });
  
  // NOTE: We removed the code that deletes conversations from explicitlyRead
  // based on CometChat counts. Only handleIncomingUnread (real-time listener)
  // should remove conversations from the explicitly read set.
  
  logInfo('🎯 Final filtered unread counts in tutor header', {
    beforeFilter: Object.keys(unreadMap).length,
    afterFilter: Object.keys(filtered).length,
    filteredIds: Object.keys(filtered).slice(0, 5).map(id => id.substring(0, 8) + '***')
  });
  
  return filtered;
};

// Clean up old "recently marked read" timestamps (older than 60 seconds)
const cleanupRecentlyMarkedRead = () => {
  try {
    const stored = localStorage.getItem('elevateED_recently_marked_read_tutor');
    if (!stored) return;
    
    const recentlyMarkedRead = JSON.parse(stored);
    const now = Date.now();
    let cleaned = false;
    
    for (const [key, timestamp] of Object.entries(recentlyMarkedRead)) {
      if (now - (timestamp as number) > 60000) {
        delete recentlyMarkedRead[key];
        cleaned = true;
      }
    }
    
    if (cleaned) {
      localStorage.setItem('elevateED_recently_marked_read_tutor', JSON.stringify(recentlyMarkedRead));
      logDebug('🧹 Cleaned up old recently marked read timestamps', {
        remaining: Object.keys(recentlyMarkedRead).length
      });
    }
  } catch (error) {
    logWarn('Failed to cleanup recently marked read timestamps', { error });
  }
};


const handleIncomingUnread = (userId: string, senderUid: string) => {
  void (async () => {
    try {
      const normalizedSender = String(senderUid || '').toLowerCase();
      const normalizedUserId = String(userId || '').toLowerCase();
      
      // Skip self-conversations (tutor messaging themselves)
      if (normalizedSender === normalizedUserId) {
        logDebug('Ignoring unread increment for self-conversation', { senderUid });
        return;
      }
      
      if (!contactIds.value.size) {
        await syncContactIds(userId);
      }
      
      if (!contactIds.value.has(normalizedSender)) {
        logDebug('Ignoring unread increment for non-contact sender', { senderUid });
        return;
      }
      
      // If this conversation was explicitly marked as read, remove it from the set
      // because there are NEW messages now
      // IMPORTANT: Always reload from localStorage to avoid overwriting changes from other components
      const explicitlyRead = getExplicitlyReadConversations();
      if (explicitlyRead.has(normalizedSender)) {
        explicitlyRead.delete(normalizedSender);
        try {
          localStorage.setItem(EXPLICITLY_READ_KEY, JSON.stringify([...explicitlyRead]));
          logInfo('🆕 Removed conversation from explicitly read due to NEW incoming message', {
            senderId: senderUid.substring(0, 8) + '***',
            remainingCount: explicitlyRead.size
          });
        } catch (error) {
          logWarn('Failed to update explicitly read conversations', { error });
        }
      }
      
      // Immediately increment the unread count in the store
      // This ensures the badge updates instantly when a message arrives
      const timestamp = Date.now();
      messagingStore.handleNewMessage(normalizedSender, '[New Message]', timestamp, normalizedSender);
      
      // Broadcast to other tabs
      broadcastUnreadCountsUpdate(Object.fromEntries(messagingStore.unreadCounts));
      
      logInfo('📨 Badge updated for incoming message in tutor header', {
        senderId: senderUid.substring(0, 8) + '***',
        newBadgeCount: messagingStore.unreadConversationCount
      });
    } catch (error) {
      logWarn('Failed processing incoming unread for tutor badge', { error, senderUid });
    }
  })();
};

// Mobile menu state
const isMobileMenuOpen = ref(false);

// User menu state
const isUserMenuOpen = ref(false);
const menuRef = ref<HTMLElement | null>(null);

// Auth/user state
const firebaseUser = ref<any | null>(null);
const storedUser = ref<any | null>(null);
const userData = ref<any | null>(null);

// Persistent display name that doesn't reset during navigation
const persistentDisplayName = ref<string>(getInitialDisplayName());

// Cache for display name to persist across navigation
const DISPLAY_NAME_CACHE_KEY = "elevateED_tutor_display_name";

// Function to get initial display name from existing data
function getInitialDisplayName(): string {
  // First check sessionStorage for cached display name (persists across navigation)
  try {
    const cachedName = sessionStorage.getItem(DISPLAY_NAME_CACHE_KEY);
    if (cachedName && cachedName !== "null" && cachedName !== "undefined") {
      const name = JSON.parse(cachedName);
      if (name && !looksLikeUID(name) && !name.includes("@") && name !== "Loading...") {
        return name;
      }
    }
  } catch {}

  // Check localStorage for existing user data first
  try {
    const storedUserData = JSON.parse(localStorage.getItem("user") || "null");
    if (storedUserData) {
      if (storedUserData.firstName && storedUserData.lastName) {
        const name = `${storedUserData.firstName} ${storedUserData.lastName}`;
        sessionStorage.setItem(DISPLAY_NAME_CACHE_KEY, JSON.stringify(name));
        return name;
      }
      if (storedUserData.username || storedUserData.name) {
        const name = storedUserData.username || storedUserData.name;
        if (!looksLikeUID(name) && !name.includes("@")) {
          sessionStorage.setItem(DISPLAY_NAME_CACHE_KEY, JSON.stringify(name));
          return name;
        }
      }
    }
  } catch {}

  // Check if we have auth token indicating logged-in state
  const hasToken = !!localStorage.getItem("token");
  const hasUser = !!localStorage.getItem("user");

  // If we have auth indicators but no name yet, show a placeholder that's not "Guest"
  if (hasToken || hasUser) {
    return "Loading...";
  }

  return "Guest";
}

function looksLikeUID(str: string): boolean {
  return !!(str && str.length > 20 && /^[a-zA-Z0-9]+$/.test(str));
}

// Scroll state
const isScrolled = ref(false);

// Add scroll event listener
const handleScroll = () => {
  isScrolled.value = window.scrollY > 10;
};

// Check if current route is login-related
const isLoginRoute = computed(() => {
  const path = route.path.toLowerCase();
  return path.includes("/login") || path.includes("login");
});

// Is authenticated
const isLoggedIn = computed(() => {
  // Check if we have actual user data, not just a token
  return (
    !!firebaseUser.value ||
    !!storedUser.value ||
    (!!localStorage.getItem("token") && !!localStorage.getItem("user"))
  );
});

// Function to update display name only when we have valid name data
const updateDisplayName = () => {
  // Don't update if we already have a valid cached name
  if (persistentDisplayName.value && 
      persistentDisplayName.value !== "Guest" && 
      persistentDisplayName.value !== "Loading...") {
    return;
  }

  // First check if we have Firebase user data with first and last name
  if (userData.value && userData.value.firstName && userData.value.lastName) {
    const name = `${userData.value.firstName} ${userData.value.lastName}`;
    persistentDisplayName.value = name;
    sessionStorage.setItem(DISPLAY_NAME_CACHE_KEY, JSON.stringify(name));
    return;
  }

  // Check stored user data for name fields
  if (storedUser.value) {
    if (storedUser.value.firstName && storedUser.value.lastName) {
      const name = `${storedUser.value.firstName} ${storedUser.value.lastName}`;
      persistentDisplayName.value = name;
      sessionStorage.setItem(DISPLAY_NAME_CACHE_KEY, JSON.stringify(name));
      return;
    }
    if (storedUser.value.username || storedUser.value.name) {
      const name = storedUser.value.username || storedUser.value.name;
      if (!looksLikeUID(name) && !name.includes("@")) {
        persistentDisplayName.value = name;
        sessionStorage.setItem(DISPLAY_NAME_CACHE_KEY, JSON.stringify(name));
        return;
      }
    }
  }

  // Check localStorage for user data with name fields
  try {
    const parsed = JSON.parse(localStorage.getItem("user") || "null");
    if (parsed) {
      if (parsed.firstName && parsed.lastName) {
        const name = `${parsed.firstName} ${parsed.lastName}`;
        persistentDisplayName.value = name;
        sessionStorage.setItem(DISPLAY_NAME_CACHE_KEY, JSON.stringify(name));
        return;
      }
      if (parsed.username || parsed.name) {
        const name = parsed.username || parsed.name;
        if (!looksLikeUID(name) && !name.includes("@")) {
          persistentDisplayName.value = name;
          sessionStorage.setItem(DISPLAY_NAME_CACHE_KEY, JSON.stringify(name));
          return;
        }
      }
    }
  } catch {}

  // Only use Firebase display name if it's not an email and we don't have other data
  if (firebaseUser.value) {
    const displayName = firebaseUser.value.displayName;
    if (displayName && !displayName.includes("@") && !looksLikeUID(displayName)) {
      persistentDisplayName.value = displayName;
      sessionStorage.setItem(DISPLAY_NAME_CACHE_KEY, JSON.stringify(displayName));
      return;
    }
  }

  // Only set to Guest if user is not logged in (don't change from Loading if still logged in)
  if (!isLoggedIn.value) {
    persistentDisplayName.value = "Guest";
    sessionStorage.removeItem(DISPLAY_NAME_CACHE_KEY);
  }
  // If logged in but no name data yet, keep existing value (could be "Loading...")
};

// Derive display name - just return the persistent value
const userDisplayName = computed(() => {
  return persistentDisplayName.value;
});

// Navigation functions

const navigateToLogin = () => {
  router.push({ name: "login" });
  closeMobileMenu();
};

// User menu handlers
const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value;
};

const closeUserMenu = () => {
  isUserMenuOpen.value = false;
};

const onDocumentClick = (event: MouseEvent) => {
  const target = event.target as Node;
  if (menuRef.value && !menuRef.value.contains(target)) {
    isUserMenuOpen.value = false;
  }
};

const handleChangePassword = async () => {
  closeUserMenu();

  // Get user email from Firebase user or stored user
  let userEmail = "";
  if (firebaseUser.value) {
    userEmail = firebaseUser.value.email;
  } else if (storedUser.value) {
    userEmail = storedUser.value.email;
  } else {
    try {
      const parsed = JSON.parse(localStorage.getItem("user") || "null");
      if (parsed) {
        userEmail = parsed.email;
      }
    } catch {}
  }

  if (!userEmail) {
    showError("Unable to find user email. Please log out and log back in.");
    return;
  }

  try {
    // Get the current domain for the action URL
    const actionUrl = `${window.location.origin}/password-reset`;

    await sendPasswordResetEmail(auth, userEmail, {
      url: actionUrl,
      handleCodeInApp: false,
    });

    showSuccess(
      "Password reset email sent successfully! Check your inbox and spam folder for the reset link."
    );
  } catch (error: any) {
    console.error("Password reset error:", error);

    if (error.code === "auth/user-not-found") {
      showError("No account found with this email address.");
    } else if (error.code === "auth/invalid-email") {
      showError("Invalid email address.");
    } else if (error.code === "auth/too-many-requests") {
      showError("Too many requests. Please try again later.");
    } else {
      showError("Failed to send reset email. Please try again.");
    }
  }
};

const handleLogout = async () => {
  // Immediately set to loading state to prevent "Guest" flash
  persistentDisplayName.value = "Logging out...";
  
  try {
    // Cleanly logout CometChat to avoid stale session on next login
    await logoutCometChat();
    await signOut(auth);
  } catch (error) {
    logError("Firebase signout error", { error });
  }

  // Clear all localStorage data
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("authToken");
  localStorage.removeItem("userRole");
  localStorage.clear();

  // Clear sessionStorage cache
  sessionStorage.removeItem(DISPLAY_NAME_CACHE_KEY);

  // Clear user state
  firebaseUser.value = null;
  storedUser.value = null;
  userData.value = null;
  messagingStore.reset();

  if (unsubscribeUnreadListener) {
    unsubscribeUnreadListener();
    unsubscribeUnreadListener = null;
  }

  closeUserMenu();

  // Use router.replace() to prevent back button navigation to dashboard
  // This also clears the navigation history
  router.replace({ name: "login" });
};

// Fetch user data from Firebase users collection
const fetchUserData = async (userId: string) => {
  try {
    const userDocRef = doc(db, "users", userId);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      userData.value = userDoc.data();
      // Update display name when user data is fetched
      updateDisplayName();
    }
  } catch (error) {
  logError("Error fetching user data for tutor header", { error });
  }
};

// Track initialization state
let isInitialized = false;
let initializationPromise: Promise<void> | null = null;
let unsubscribeUnreadListener: (() => void) | null = null;

const getCometChatDisplayName = () => {
  const nameCandidates = [
    userDisplayName.value,
    `${userData.value?.firstName || ""} ${userData.value?.lastName || ""}`.trim(),
    storedUser.value?.name,
    storedUser.value?.username,
  ].filter(Boolean) as string[];
  const name = nameCandidates.find(n => n && n !== "Guest" && n !== "Loading...");
  return name || "User";
};

// Initialize unread message counts for the badge
const initializeUnreadMessageCounts = async (userId: string, force = false) => {
  // Prevent multiple simultaneous initializations unless forced
  if (!force && (isInitialized || initializationPromise)) {
    if (initializationPromise) {
      await initializationPromise;
    }
    return;
  }

  initializationPromise = (async () => {
    try {
      logDebug('Initializing unread message counts for tutor', { userId: userId.substring(0, 8) + '***', force });

      // Always reset counts before initializing to avoid stale localStorage data
      messagingStore.reset();

      // Ensure CometChat session is ready
      await ensureInitializedAndLoggedIn(userId, getCometChatDisplayName());

      // Sync contact IDs for filtering
      if (auth.currentUser?.uid && !contactIds.value.size) {
        await syncContactIds(auth.currentUser.uid);
      }

      // Seed unread counts from CometChat, filtered to contacts only
      const rawUnreadMap = await fetchUnreadMessageCountMap();
      const filteredUnreadMap = filterToContacts(rawUnreadMap);
      messagingStore.setUnreadCounts(filteredUnreadMap);
      broadcastUnreadCountsUpdate(filteredUnreadMap);
      isInitialized = true;

      logInfo('Unread message counts initialized for tutor via CometChat', { 
        userId: userId.substring(0, 8) + '***',
        conversationsWithUnread: Object.keys(filteredUnreadMap).length
      });
    } catch (error) {
      logWarn('Falling back to messaging contacts for tutor unread initialization', {
        userId: userId.substring(0, 8) + '***',
        error
      });

      try {
        const messagingContacts = await messagingPermissionService.getTutorMessagingContacts(userId);
        await messagingStore.initialize(userId, 'tutor');
        isInitialized = true;
        logInfo('Unread message counts initialized for tutor via contacts fallback', { 
          contactsCount: messagingContacts.length,
          userId: userId.substring(0, 8) + '***'
        });
      } catch (fallbackError) {
        logError('Failed to initialize unread counts for tutor from fallback contacts', {
          userId: userId.substring(0, 8) + '***',
          error: fallbackError
        });
      }
    } finally {
      // Always set unreadReady to true after initialization attempt completes
      // This prevents badge from showing stale/incorrect counts during initial load
      unreadReady.value = true;
      initializationPromise = null;
    }
  })();

  await initializationPromise;
};

// Debounce state for refresh
let refreshTimeout: number | null = null;
let isRefreshing = false;

// Refresh unread message counts periodically
const refreshUnreadMessageCounts = async (userId: string) => {
  // Prevent multiple simultaneous refresh calls
  if (isRefreshing) {
    logDebug('Refresh already in progress, skipping', { userId: userId.substring(0, 8) + '***' });
    return;
  }
  
  // Clear any pending refresh
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }
  
  // Debounce the refresh call
  refreshTimeout = setTimeout(async () => {
    try {
      isRefreshing = true;
      
      try {
        await ensureInitializedAndLoggedIn(userId, getCometChatDisplayName());
        if (auth.currentUser?.uid && !contactIds.value.size) {
          await syncContactIds(auth.currentUser.uid);
        }
        const rawUnreadMap = await fetchUnreadMessageCountMap();
        const filteredUnreadMap = filterToContacts(rawUnreadMap);
        messagingStore.setUnreadCounts(filteredUnreadMap);
        broadcastUnreadCountsUpdate(filteredUnreadMap);
        logDebug('Refreshed unread message counts for tutor via CometChat', { 
          userId: userId.substring(0, 8) + '***',
          conversationsWithUnread: Object.keys(filteredUnreadMap).length
        });
      } catch (cometchatError) {
        logWarn('CometChat refresh failed for tutor, using contacts fallback', { cometchatError });
        const messagingContacts = await messagingPermissionService.getTutorMessagingContacts(userId);
        await messagingStore.initialize(userId, 'tutor');
        logDebug('Refreshed unread message counts for tutor via contacts fallback', { 
          contactsCount: messagingContacts.length,
          userId: userId.substring(0, 8) + '***'
        });
      }
    } catch (error) {
      logDebug('Failed to refresh unread message counts for tutor', {
        userId: userId.substring(0, 8) + '***',
        error
      });
    } finally {
      isRefreshing = false;
      refreshTimeout = null;
    }
  }, 1000); // 1 second debounce
};

// Dev helper to inspect unread map on demand
if (typeof window !== 'undefined') {
  (window as any).debugTutorUnreadCounts = async () => {
    try {
      await ensureInitializedAndLoggedIn(auth.currentUser?.uid || '', getCometChatDisplayName());
      if (auth.currentUser?.uid && !contactIds.value.size) {
        await syncContactIds(auth.currentUser.uid);
      }
      const rawUnreadMap = await fetchUnreadMessageCountMap();
      const filteredUnreadMap = filterToContacts(rawUnreadMap);
      return {
        message: 'Unread sync triggered for tutor',
        conversationsWithUnread: Object.keys(filteredUnreadMap).length,
        conversationIds: Object.keys(filteredUnreadMap),
        rawUnreadMap,
        filteredUnreadMap,
      };
    } catch (error) {
      logWarn('debugTutorUnreadCounts failed', { error });
      return { message: 'Failed to sync unread counts', error };
    }
  };

  // Helper to inspect contacts vs unread map (raw and filtered)
  (window as any).debugTutorContactsAndUnread = async () => {
    try {
      await ensureInitializedAndLoggedIn(auth.currentUser?.uid || '', getCometChatDisplayName());
      const contacts = auth.currentUser
        ? await messagingPermissionService.getTutorMessagingContacts(auth.currentUser.uid)
        : [];
      const contactIdsArr = contacts.map((c: any) => c.id);
      const rawUnread = await fetchUnreadMessageCountMap();
      const filteredUnread = filterToContacts(rawUnread);
      return {
        contactsCount: contactIdsArr.length,
        contactIds: contactIdsArr,
        rawUnreadCount: Object.keys(rawUnread || {}).length,
        rawUnread,
        filteredUnreadCount: Object.keys(filteredUnread || {}).length,
        filteredUnread,
      };
    } catch (error) {
      logWarn('debugTutorContactsAndUnread failed', { error });
      return { message: 'Failed to load contacts/unread', error };
    }
  };

  // Helper to clear unread for a specific ghost conversation
  (window as any).clearTutorGhostConversation = async (conversationId: string) => {
    try {
      await ensureInitializedAndLoggedIn(auth.currentUser?.uid || '', getCometChatDisplayName());
      logInfo('Clearing tutor ghost conversation', { conversationId });
      
      // Read receipts disabled - not marking messages as read in CometChat
      logDebug('Read receipts disabled - tutor ghost conversation cleared locally only', {
        conversationId: conversationId.substring(0, 8) + '***'
      });
      
      // Force-clear this conversation from local unread state
      messagingStore.markAsRead(conversationId.toLowerCase());
      
      // Re-sync unread counts after clearing
      if (auth.currentUser?.uid) {
        await refreshUnreadMessageCounts(auth.currentUser.uid);
      }
      
      return {
        success: true,
        conversationId,
        messagesMarkedAsRead: 0,
        message: `Cleared tutor ghost conversation (local only - read receipts disabled)`
      };
    } catch (error) {
      logError('Failed to clear tutor ghost conversation', { conversationId, error });
      // Even if CometChat fails, clear it locally
      messagingStore.markAsRead(conversationId.toLowerCase());
      return {
        success: true,
        conversationId,
        clearedLocally: true,
        error: error instanceof Error ? error.message : String(error),
        message: 'Cleared tutor ghost conversation locally (CometChat operation failed)'
      };
    }
  };
}

// Page visibility listener to refresh when tab becomes active
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible' && auth.currentUser) {
    logDebug('Page became visible, refreshing unread counts');
    initializeUnreadMessageCounts(auth.currentUser.uid, true); // Force refresh
  }
};

// Init
onMounted(() => {
  // Load stored user from localStorage (set during login)
  try {
    storedUser.value = JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    storedUser.value = null;
  }

  // Update display name initially
  updateDisplayName();

  // Listen to Firebase auth changes
  onAuthStateChanged(auth, async (user) => {
    firebaseUser.value = user;
    if (user) {
      // Fetch additional user data from Firestore
      await fetchUserData(user.uid);
    } else {
      // Clear user data and keep storedUser in case backend login info exists
      userData.value = null;
      try {
        storedUser.value = JSON.parse(localStorage.getItem("user") || "null");
      } catch {
        storedUser.value = null;
      }
    }
    // Update display name after auth state change
    updateDisplayName();
    
    // Initialize unread message counts when user is authenticated
    if (user) {
      try {
        await initializeUnreadMessageCounts(user.uid);
        
        // Set up CometChat unread listener AFTER initialization succeeds
        // This ensures CometChat is logged in before we register the listener
        const listenerId = `tutor-header-unread-${user.uid}`;
        if (unsubscribeUnreadListener) {
          unsubscribeUnreadListener();
        }
        unsubscribeUnreadListener = subscribeToUnreadMessages(listenerId, user.uid, (senderUid) => {
          handleIncomingUnread(user.uid, senderUid);
        });
        
        logInfo('✅ CometChat message listener registered in tutor header', {
          listenerId,
          userId: user.uid.substring(0, 8) + '***'
        });
      } catch (error) {
        logError('❌ Failed to initialize messaging in tutor header', { error });
        
        // Even if initialization fails, try to set up the listener
        // It might work if CometChat was already initialized
        try {
          const listenerId = `tutor-header-unread-${user.uid}`;
          if (unsubscribeUnreadListener) {
            unsubscribeUnreadListener();
          }
          unsubscribeUnreadListener = subscribeToUnreadMessages(listenerId, user.uid, (senderUid) => {
            handleIncomingUnread(user.uid, senderUid);
          });
          logInfo('⚠️ Listener registered despite init error', { listenerId });
        } catch (listenerError) {
          logError('❌ Failed to register message listener', { listenerError });
        }
      }
      
      // Set up periodic refresh of unread counts (every 30 seconds)
      const refreshInterval = setInterval(() => {
        if (auth.currentUser) {
          refreshUnreadMessageCounts(auth.currentUser.uid);
          // Also cleanup old timestamps
          cleanupRecentlyMarkedRead();
        }
      }, 30000);
      
      // Store interval for cleanup
      (window as any).__tutorUnreadRefreshInterval = refreshInterval;
      
      // Set up periodic check to ensure listener is still registered (every 60 seconds)
      // This helps recover from any CometChat connection issues
      const listenerCheckInterval = setInterval(() => {
        if (auth.currentUser && !unsubscribeUnreadListener) {
          logWarn('⚠️ Message listener lost, re-registering...', {
            userId: auth.currentUser.uid.substring(0, 8) + '***'
          });
          
          const listenerId = `tutor-header-unread-${auth.currentUser.uid}`;
          const currentUid = auth.currentUser.uid;
          
          unsubscribeUnreadListener = subscribeToUnreadMessages(listenerId, currentUid, (senderUid) => {
            handleIncomingUnread(currentUid, senderUid);
          });
          
          logInfo('✅ Message listener re-registered', { listenerId });
        }
      }, 60000);
      
      // Store interval for cleanup
      (window as any).__tutorListenerCheckInterval = listenerCheckInterval;
      
      // Listen for unread state changes from MessagesView to keep header in sync
      const handleUnreadStateChange = (event: CustomEvent) => {
        const { conversationId, action } = event.detail || {};
        logInfo('🔔 Tutor header received unread-state-changed event', {
          conversationId: conversationId?.substring(0, 8) + '***',
          action
        });
        
        if (action === 'marked-read' && conversationId) {
          // Immediately mark as read in the store
          messagingStore.markAsRead(conversationId);
          broadcastConversationRead(conversationId);
        }
      };
      
      window.addEventListener('unread-state-changed', handleUnreadStateChange as EventListener);
      
      // Store for cleanup
      (window as any).__tutorUnreadStateChangeListener = handleUnreadStateChange;
    }
  });

  // Additional initialization on page load (in case auth state change missed)
  if (auth.currentUser) {
    const currentAuthUser = auth.currentUser; // Capture to prevent null access after async operations
    (async () => {
      try {
        await initializeUnreadMessageCounts(currentAuthUser.uid);
      } catch (error) {
        logWarn('Initial unread init on mount failed for tutor', { error });
      }
      // Check if still authenticated after async operations
      if (!auth.currentUser) {
        logWarn('User logged out during unread initialization');
        return;
      }
      const listenerId = `tutor-header-unread-${currentAuthUser.uid}`;
      if (unsubscribeUnreadListener) {
        unsubscribeUnreadListener();
      }
      unsubscribeUnreadListener = subscribeToUnreadMessages(listenerId, currentAuthUser.uid, (senderUid) => {
        handleIncomingUnread(currentAuthUser.uid, senderUid);
      });
    })();
  }

  document.addEventListener("click", onDocumentClick);
  window.addEventListener("scroll", handleScroll, { passive: true });
  document.addEventListener("visibilitychange", handleVisibilityChange);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", onDocumentClick);
  window.removeEventListener("scroll", handleScroll);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  
  // Clean up unread refresh interval
  if ((window as any).__tutorUnreadRefreshInterval) {
    clearInterval((window as any).__tutorUnreadRefreshInterval);
    delete (window as any).__tutorUnreadRefreshInterval;
  }
  
  // Clean up listener check interval
  if ((window as any).__tutorListenerCheckInterval) {
    clearInterval((window as any).__tutorListenerCheckInterval);
    delete (window as any).__tutorListenerCheckInterval;
  }
  
  // Clean up unread state change listener
  const unreadStateListener = (window as any).__tutorUnreadStateChangeListener;
  if (unreadStateListener) {
    window.removeEventListener('unread-state-changed', unreadStateListener);
    delete (window as any).__tutorUnreadStateChangeListener;
  }

  if (unsubscribeUnreadListener) {
    unsubscribeUnreadListener();
    unsubscribeUnreadListener = null;
  }
  
  // Clean up refresh timeout
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }
});

// Toggle mobile menu
const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

// Close mobile menu
const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
};
</script>

<style scoped>
/* Add padding to body to account for fixed header */
body {
  padding-top: 120px;
}

.header {
  width: 100%;
  padding: 16px 24px;
  background: transparent;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
}

.header-container {
  display: flex;
  align-items: center;
  width: 80em;
  height: 6em;
  margin: 0 auto;
  margin-top: 1.25em;
  background: #5271ff !important;
  border-radius: 50px;
  padding: 12px 24px;
  border: 3px solid #000;
  box-shadow: 8px 8px 0px #000;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  position: relative;
}

/* Blur Effect for Header - Always active for better performance */
.header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  background-color: rgba(255, 255, 255, 0.1);
  z-index: -1;
}

/* Optional: Add subtle opacity change on scroll for visual feedback */
.header.scrolled::before {
  background-color: rgba(255, 255, 255, 0.15);
}

/* Logo Section */
.logo-section {
  height: auto;
  width: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tutorlogo-image {
  height: 50px !important;
  padding-top: 5px;
  width: auto;
  object-fit: contain;
  max-width: 100%;
}

.logo-icon {
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.logo-text {
  font-size: 20px;
  font-weight: 600;
  color: #000;
  letter-spacing: -0.02em;
}

/* Navigation */
.navigation {
  display: flex;
  align-items: center;
  gap: 32px;
  margin-left: auto;
  margin-right: 24px;
}

.nav-link {
  font-size: 18px;
  font-weight: 500;
  color: #ffffff;
  text-decoration: none;
  padding: 8px 0;
  position: relative;
  transition: all 0.2s ease;
}

.nav-link:hover {
  color: #ffffff;
  font-weight: 700;
  background: none !important;
}

.nav-link:hover::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 3px;
  background: #ffffff !important;
  border-radius: 2px;
}

.nav-link.active {
  font-weight: 700;
  color: #ffffff;
}

.nav-link.active::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 5px;
  background: #ffffff !important;
  border-radius: 10px;
}

/* Ensure no background colors on any nav-link states */
.nav-link,
.nav-link:hover,
.nav-link:focus,
.nav-link:active,
.nav-link.active {
  background: transparent !important;
  background-color: transparent !important;
}

/* Mobile Hamburger Menu */
.mobile-menu-toggle {
  display: none;
  flex-direction: column;
  justify-content: space-around;
  width: 25px;
  height: 25px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
}

.hamburger-line {
  width: 25px;
  height: 3px;
  background: #000;
  border-radius: 2px;
  transition: all 0.3s ease;
}

/* Mobile Navigation */
.mobile-nav {
  display: none;
  position: absolute;
  top: calc(100% + 0.5em);
  left: 0;
  right: 0;
  background: #5271ff;
  border: 3px solid #000;
  border-radius: 0 0 50px 50px;
  box-shadow: 8px 8px 0px #000;
  z-index: 999;
  margin: 0 auto;
  width: 100%;
}

.mobile-nav.active {
  display: block;
}

.mobile-nav-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.mobile-nav-link {
  font-size: 18px;
  font-weight: 500;
  color: #ffffff;
  text-decoration: none;
  padding: 12px 0;
  border-bottom: 2px solid #f0f0f0;
  transition: all 0.3s ease;
  background: none;
  border-top: none;
  border-left: none;
  border-right: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
}

.mobile-nav-link:hover,
.mobile-nav-link.router-link-active {
  color: #000;
  font-weight: 700;
  border-bottom-color: #000;
}

.mobile-nav-link:last-child {
  border-bottom: none;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn {
  padding: 10px 35px;
  border-radius: 24px;
  font-size: 18px;
  font-weight: 700;
  font-family: "Inter", sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;
}

.btn-login {
  background: #ffffff;
  color: rgb(0, 0, 0);
  border: 3px solid #000;
  width: 10em;
}

.btn-login:hover,
.btn-login:active,
.btn-login.active {
  background: #fcbb02;
  color: #000;
  border-color: #000;
}

.btn-register {
  background: white;
  color: #000;
  border: 3px solid #000;
}

.btn-register:hover,
.btn-register:active,
.btn-register.active {
  background: #fcbb02;
  color: #000;
  border-color: #000;
}

/* User menu */
.user-menu {
  position: relative;
}

.user-button {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border: 3px solid #000;
  color: #000;
  background: #fff;
  border-radius: 30px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 4px 4px 0px #000;
  min-width: 180px;
  justify-content: space-between;
}

.user-button:hover {
  background: #fcbb02;
  transform: translate(-2px, -2px);
  box-shadow: 6px 6px 0px #000;
}

.user-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #5271ff;
  border-radius: 50%;
  color: #fff;
}

.user-icon {
  width: 20px;
  height: 20px;
}

.user-name {
  flex: 1;
  text-align: left;
  font-weight: 600;
  color: #000;
}

.dropdown-arrow {
  color: #666;
  transition: transform 0.2s ease;
}

.user-button:hover .dropdown-arrow {
  color: #000;
}

.user-button-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f9fafb;
  border-radius: 8px;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  pointer-events: none;
  opacity: 0.7;
}

.user-dropdown {
  position: absolute;
  right: 0;
  margin-top: 8px;
  width: 200px; /* Increased width to accommodate "Change Password" */
  background: #ffffff;
  border: 1px solid rgba(25, 20, 0, 0.21);
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  z-index: 50;
  overflow: hidden;
}
.user-dropdown-link {
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  padding: 8px 16px;
  font-size: 14px;
  color: #374151;
  background: #ffffff;
  border: none;
  cursor: pointer;
  text-decoration: none;
}
.user-dropdown-link:hover {
  background: #f9fafb;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .header-container {
    width: 90%;
    max-width: calc(100vw - 48px);
  }
}

/* iPad and tablet adjustments */
@media (max-width: 900px) {
  .header-container {
    padding: 10px 16px;
  }

  .tutorlogo-image {
    height: 42px !important;
  }

  .navigation {
    gap: 20px;
    margin-right: 16px;
  }

  .nav-link {
    font-size: 15px;
  }

  .user-button {
    padding: 10px 16px;
    min-width: 140px;
    font-size: 14px;
    gap: 8px;
  }
}

@media (max-width: 900px) {
  .header-container {
    width: 95%;
    max-width: calc(100vw - 32px);
    height: 5em;
    padding: 8px 20px;
    margin-top: 1em;
    box-shadow: 6px 6px 0px #000;
    justify-content: space-between;
  }

  .logo-image {
    height: 80px;
  }

  .logo-section {
    flex: 0 0 auto;
  }

  .navigation {
    display: none;
  }

  .action-buttons {
    display: none;
  }

  .mobile-menu-toggle {
    display: flex;
    margin-left: auto;
  }
}

@media (max-width: 600px) {
  .header {
    padding: 12px 12px;
  }

  .header-container {
    width: calc(100vw - 24px);
    max-width: calc(100vw - 24px);
    height: 4em;
    padding: 6px 16px;
    margin-top: 0.8em;
    box-shadow: 5px 5px 0px #000;
  }

  .logo-image {
    height: 60px;
  }

  .navigation {
    gap: 16px;
    margin-right: 12px;
  }

  .nav-link {
    font-size: 14px;
  }

  .btn {
    padding: 6px 20px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .header {
    padding: 10px 10px;
  }

  .header-container {
    width: calc(100vw - 20px);
    max-width: calc(100vw - 20px);
    height: 3.5em;
    padding: 4px 12px;
    margin-top: 0.5em;
    box-shadow: 4px 4px 0px #000;
    justify-content: space-between;
    position: relative;
  }

  .logo-image {
    height: 45px;
  }

  .navigation,
  .action-buttons {
    display: none;
  }

  .mobile-menu-toggle {
    display: flex;
  }

  .mobile-nav {
    margin-top: 0.5em;
    box-shadow: 4px 4px 0px #000;
  }

  .mobile-nav-link {
    font-size: 16px;
    padding: 10px 0;
  }
}

.header .nav-link,
.header .nav-link:hover,
.header .nav-link:active,
.header .nav-link.active {
  color: #ffffff !important;
}

.header .mobile-nav-link,
.header .mobile-nav-link:hover,
.header .mobile-nav-link.router-link-active {
  color: #ffffff !important;
}

.header .logo-text {
  color: #ffffff !important;
}

/* Unread Messages Badge Styles */
.messages-link {
  position: relative;
}

.unread-badge {
  position: absolute;
  top: -8px;
  right: -12px;
  background: #ef4444;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: bold;
  line-height: 1;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 10;
  min-width: 20px;
  padding: 0 2px;
}

.unread-badge.mobile {
  top: -6px;
  right: -10px;
  width: 18px;
  height: 18px;
  font-size: 10px;
  min-width: 18px;
}

/* Handle larger numbers */
.unread-badge:has-text {
  border-radius: 10px;
  padding: 0 4px;
  min-width: 20px;
  width: auto;
}

.unread-badge.mobile:has-text {
  border-radius: 9px;
  padding: 0 3px;
  min-width: 18px;
  width: auto;
}

/* Animation for new messages */
@keyframes badge-pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

.unread-badge {
  animation: badge-pulse 0.3s ease-in-out;
}
</style>
